import { createSelector } from 'reselect';

import { RootState } from '@store/index';

const getPending = (state: RootState) => state.customer.pending;

const getCustomer = (state: RootState) => state.customer.data;

const getError = (state: RootState) => state.customer.error;

export const getCustomerSelector = createSelector(getCustomer, (customer) => customer);

/**
 * The customer's preferred language, as chosen on the storefront and carried
 * through the configurator onto their petcon owner record.
 *
 * Returned in petcon's own representation — uppercase `'DE' | 'EN' | 'FR'` — not
 * as a react-intl locale. `localeFromPreference` does that conversion, so the two
 * forms stay distinguishable and this selector keeps reporting exactly what the
 * backend said.
 *
 * `null` means no preference is recorded: an owner created before the field
 * existed, a customer who never went through the configurator, or a petcon lookup
 * that failed. Callers must treat that as "unknown", not as German.
 *
 * Cast because `ICustomer` lives in the `src/types` submodule and does not declare
 * the field yet. Add it there and drop the cast.
 */
export const getPreferredLanguageSelector = createSelector(getCustomer, (customer) => {
  const value = (customer as (ICustomer & { preferredLanguage?: string }) | null | undefined)
    ?.preferredLanguage;

  return value?.trim() ? value.trim().toUpperCase() : null;
});

export const getPendingSelector = createSelector(getPending, (pending) => pending);

export const getErrorSelector = createSelector(getError, (error) => error);
